import { createRoot } from 'react-dom/client';
import { ClerkProvider } from '@clerk/clerk-react';
import App from './App.tsx';
import './index.css';
import { initSentry } from './lib/sentry';
import { isAuth0Enabled } from './config/auth';

initSentry();

// Auth0 domain and SPA client ID are browser-public identifiers. These defaults keep Lovable's
// preview build functional when it does not inject Vite variables; secrets must never be added.
const auth0Domain = import.meta.env.VITE_AUTH0_DOMAIN || 'dev-enry.eu.auth0.com';
const auth0ClientId = import.meta.env.VITE_AUTH0_CLIENT_ID || '89xA27RgY5gE56gOYuj1HfKYWcjt5Ci2';

// Clerk publishable keys are browser-public identifiers. Keep server secret keys out of source.
const defaultKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const devKey =
  import.meta.env.VITE_CLERK_PUBLISHABLE_KEY_DEV ||
  'pk_test_ZHJpdmVuLWV3ZS04OS5jbGVyay5hY2NvdW50cy5kZXYk';
const prodKey =
  import.meta.env.VITE_CLERK_PUBLISHABLE_KEY_PROD ||
  'pk_live_Y2xlcmsubnRudWlwYWRlbC5ubyQ';

let publishableKey = defaultKey || '';
if (typeof window !== 'undefined') {
  const host = window.location.host.toLowerCase();
  const matchesProdDomain = /(^|\.)ntnuipadel\.no$/.test(host);

  if (matchesProdDomain && prodKey) {
    publishableKey = prodKey;
  } else if (!matchesProdDomain && devKey) {
    publishableKey = devKey;
  }
}

if (!publishableKey) {
  publishableKey = import.meta.env.PROD ? prodKey || '' : devKey || '';
}

if (!publishableKey && !isAuth0Enabled) {
  throw new Error('Missing Clerk Publishable Key');
}

const application = <App />;

async function renderApplication() {
  if (isAuth0Enabled) {
    const [{ Auth0Provider }] = await Promise.all([import('@auth0/auth0-react')]);

    createRoot(document.getElementById('root')!).render(
      <Auth0Provider
        domain={auth0Domain}
        clientId={auth0ClientId}
        authorizationParams={{
          redirect_uri: `${window.location.origin}/auth/callback`,
          scope: 'openid profile email offline_access',
        }}
        useRefreshTokens
        useRefreshTokensFallback
        onRedirectCallback={(appState) => {
          window.history.replaceState({}, document.title, appState?.returnTo || '/');
        }}
      >
        <ClerkProvider publishableKey={publishableKey} afterSignOutUrl="/clerk">
          {application}
        </ClerkProvider>
      </Auth0Provider>,
    );
    return;
  }

  createRoot(document.getElementById('root')!).render(
    <ClerkProvider publishableKey={publishableKey} afterSignOutUrl="/">
      {application}
    </ClerkProvider>
  );
}

void renderApplication();
